export const ITEMS_PER_PAGE = 24;

/**
 * Maximum number of pages to allow for SEO pagination.
 * This prevents excessive deep pagination while still providing good SEO coverage.
 *
 * Set to 1000 to allow deep indexing of large catalogs (24 items/page * 1000 = 24,000 items).
 * Pages beyond this limit return 404 to prevent performance issues and infinite crawling.
 */
export const MAX_PAGINATED_PAGES = 1000;

/**
 * Checks if a URL path contains pagination segments (e.g., /page/2, /page/3).
 * Used to detect and block pagination URLs on pages that should only use infinite scroll.
 *
 * @param pathSegments - Array of URL path segments (e.g., ['music', 'category', 'lo-fi', 'page', '2'])
 * @returns true if the path contains a pagination segment, false otherwise
 *
 * @example
 * isPaginationPath(['music', 'category', 'lo-fi']) // false
 * isPaginationPath(['music', 'category', 'lo-fi', 'page', '2']) // true
 * isPaginationPath(['page', '2']) // true
 */
export const isPaginationPath = (pathSegments: string[] | string): boolean => {
  const pathArray = Array.isArray(pathSegments) ? pathSegments : [pathSegments];
  return pathArray.length >= 2 && pathArray[pathArray.length - 2] === 'page';
};

/**
 * Calculate the maximum number of pages based on total count
 *
 * Uses ceil to ensure all results are accessible. Even if the last page has fewer items
 * due to interspersing, we removed the hasResults check in SSR so empty pages won't 404.
 * The lenient validation (page > totalPages + 1) provides a buffer for edge cases.
 *
 * Note: The result is capped at MAX_PAGINATED_PAGES to prevent excessive pagination.
 */
export const calculateMaxPages = (
  totalCount: number,
  perPage: number = ITEMS_PER_PAGE
): number => {
  const calculatedPages = Math.ceil(totalCount / perPage);
  return Math.min(calculatedPages, MAX_PAGINATED_PAGES);
};

/**
 * Generate previous and next pagination URLs
 */
export const getPaginationUrls = (
  basePath: string,
  currentPage: number,
  totalPages: number
): { prev?: string; next?: string } => ({
  prev:
    currentPage > 1
      ? currentPage === 2
        ? basePath.replace('/page', '')
        : `${basePath}/${currentPage - 1}`
      : undefined,
  next: currentPage < totalPages ? `${basePath}/${currentPage + 1}` : undefined,
});
